// Repo-owned React binding for ClientObservability. Resolves the singleton
// lazily and returns null until the async import + init completes. Any
// client component may use this — see redacting-observability.ts / #85's
// `streaming.console.*` signals for the client-instrumentation precedent
// this hook now serves beyond its original live-in-club row.
//
// `createClientOnlyFn` gates the dynamic import so the Tanstack Start
// import-protection plugin doesn't reject this module from the server
// bundle (matches the pattern used in src/routes/__root.tsx). On the
// server the wrapped function returns undefined and the hook stays null.

import { createClientOnlyFn } from '@tanstack/react-start';
import { useEffect, useState } from 'react';

import type { ClientObservability } from '../ports';

export type ClientObservabilityLike = Pick<
	ClientObservability,
	'log' | 'reportError'
>;

const resolveClientObservability = createClientOnlyFn(
	async (): Promise<ClientObservabilityLike> => {
		const mod = await import('../composition.client');
		return mod.getClientObservability();
	},
);

export function useClientObservability(): ClientObservabilityLike | null {
	const [obs, setObs] = useState<ClientObservabilityLike | null>(null);
	useEffect(() => {
		let cancelled = false;
		const pending = resolveClientObservability();
		/* v8 ignore start — server path returns undefined; in jsdom the
		   composition.client import goes through but the dynamic init is
		   non-deterministic. Covered indirectly by real-browser e2e. */
		if (!pending) return;
		pending
			.then((resolved) => {
				if (!cancelled) setObs(resolved);
			})
			.catch(() => {
				if (!cancelled) setObs(null);
			});
		/* v8 ignore stop */
		return () => {
			cancelled = true;
		};
	}, []);
	return obs;
}
